import { createContext } from 'react';

import { createAccountResource } from './resources/accountResource';
import { createAccountUserResource } from './resources/accountUserResource';
import { createAiCallResource } from './resources/aiCallResource';
import { createAppConfigResource } from './resources/appConfigResource';
import { createAppointmentHistoryResource } from './resources/appointmentHistoryResource';
import { createAppointmentResource } from './resources/appointmentResource';
import { createAppointmentWindowResource } from './resources/appointmentWindowResource';
import { createCallCenterResource } from './resources/callCenterResource';
import { createCallSegmentResource } from './resources/callSegmentResource';
import { createCompanyDataProviderResource } from './resources/companyDataProviderResource';
import { createCompanyResource } from './resources/companyResource';
import { createCompanyWindowResource } from './resources/companyWindowResource';
import { createEmployeeResource } from './resources/employeeResource';
import { createServiceTitanMappedCalls } from './resources/mappedCallResource';
import { createPhoneCallV1Resource } from './resources/phoneCallV1Resource';
import { createPointmanCustomerResource } from './resources/pointmanCustomerResource';
import { createPoolBrainCustomerLocationsResource } from './resources/poolBrainCustomerLocationsResource';
import { createPoolBrainCustomerResource } from './resources/poolBrainCustomerResource';
import { createPoolBrainPhoneCallResource } from './resources/poolBrainPhoneCallResource';
import { createServiceTitanAdjustableCapacityResource } from './resources/serviceTitanAdjustableCapacityResource';
import { createServiceTitanApiPhoneCallResource } from './resources/serviceTitanApiPhoneCallResource';
import { createServiceTitanAppointmentResource } from './resources/serviceTitanAppointmentResource';
import { serviceTitanBusinessUnitResource } from './resources/serviceTitanBusinessUnitResource';
import { createServiceTitanCapacityPhoneCallResource } from './resources/serviceTitanCapacityPhoneCallResource';
import { createServiceTitanCustomerResource } from './resources/serviceTitanCustomerResource';
import { createServiceTitanJobCancelReasonResource } from './resources/serviceTitanJobCancelReasonResource';
import { createServiceTitanJobResource } from './resources/serviceTitanJobResource';
import { createServiceTitanJobTypeResource } from './resources/serviceTitanJobTypeResource';
import { createServiceTitanLocationResource } from './resources/serviceTitanLocationResource';
import { createServiceTitanMembershipTypeResource } from './resources/serviceTitanMembershipTypeResource';
import { createServiceTitanPortalV2PhoneCallResource } from './resources/serviceTitanPortalV2PhoneCallResource';
import { createServiceTitanTechnicianResource } from './resources/serviceTitanTechnicianResource';
import { createServiceTitanZoneResource } from './resources/serviceTitanZoneResource';
import { createUserResource } from './resources/userResource';

// TODO - ServiceTitan Resources should likely be a separate and dynamic context that is updated when the companyId
// changes OR something that can be cached.
const createServiceTitanResources = (companyId: string) => ({
  appointments: createServiceTitanAppointmentResource(companyId),
  adjustableCapacity: createServiceTitanAdjustableCapacityResource(companyId),
  businessUnits: serviceTitanBusinessUnitResource(companyId),
  customers: createServiceTitanCustomerResource(companyId),
  jobCancelReasons: createServiceTitanJobCancelReasonResource(companyId),
  jobs: createServiceTitanJobResource(companyId),
  jobTypes: createServiceTitanJobTypeResource(companyId),
  locations: createServiceTitanLocationResource(companyId),
  membershipTypes: createServiceTitanMembershipTypeResource(companyId),
  technicians: createServiceTitanTechnicianResource(companyId),
  mappedCalls: createServiceTitanMappedCalls(companyId),
  zones: createServiceTitanZoneResource(companyId)
});

const createPoolBrainResources = (companyId: string) => ({
  customerLocations: createPoolBrainCustomerLocationsResource(companyId),
  customers: createPoolBrainCustomerResource(companyId)
});

export const ApiResources = {
  accounts: createAccountResource(),
  accountUsers: createAccountUserResource(),
  appointments: createAppointmentResource(),
  appointmentHistory: createAppointmentHistoryResource(),
  appointmentWindows: createAppointmentWindowResource(),
  callCenters: createCallCenterResource(),
  callSegments: createCallSegmentResource(),
  companies: createCompanyResource(),
  companyDataProviders: createCompanyDataProviderResource(),
  companyWindows: createCompanyWindowResource(),
  employees: createEmployeeResource(),
  phoneCallsV1: createPhoneCallV1Resource(),
  pointmanCustomers: createPointmanCustomerResource(),
  serviceTitanApiPhoneCalls: createServiceTitanApiPhoneCallResource(),
  serviceTitanCapacityPhoneCalls: createServiceTitanCapacityPhoneCallResource(),
  serviceTitanPortalPhoneCallsV2: createServiceTitanPortalV2PhoneCallResource(),
  poolBrainPhoneCalls: createPoolBrainPhoneCallResource(),
  serviceTitan: createServiceTitanResources,
  poolBrain: createPoolBrainResources,
  users: createUserResource(),
  appConfig: createAppConfigResource(),
  aiCalls: createAiCallResource()
} as const;

export const ApiContext = createContext(ApiResources);
